import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
import { useSelectedPlan, PLANS, PlanId, Plan } from './PlanContext';
interface PlanSelectorProps {
  variant?: 'full' | 'compact';
}
const PlanCard = ({
  plan,
  selected,
  onSelect,
  variant
}: {
  plan: Plan;
  selected: boolean;
  onSelect: () => void;
  variant: 'full' | 'compact';
}) => {
  const isFeatured = plan.featured;
  const showBenefits = variant === 'full';
  return <motion.button type="button" onClick={onSelect} whileTap={{
    scale: 0.99
  }} animate={{
    scale: selected ? 1 : 0.98,
    opacity: selected ? 1 : 0.75
  }} transition={{
    duration: 0.2
  }} className={`relative text-left rounded-2xl p-4 md:p-5 flex flex-col w-full transition-all duration-300 ${selected ? isFeatured ? 'bg-gradient-to-br from-cyan/[0.08] to-gris-oscuro border-2 border-cyan shadow-[0_0_32px_rgba(0,229,255,0.18)]' : 'bg-gris-oscuro border-2 border-cyan/70 shadow-[0_0_20px_rgba(0,229,255,0.1)]' : 'bg-gris-oscuro border-2 border-gris-medio hover:border-cyan/30'}`} data-id="element-270">
      {isFeatured && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10.5px] md:text-[11px] font-extrabold px-3 py-1 rounded-full whitespace-nowrap tracking-widest uppercase transition-colors ${selected ? 'bg-cyan text-black' : 'bg-cyan/30 text-cyan'}`} data-id="element-271">
          ⭐ Más elegido
        </span>}

      <div className="flex items-start justify-between gap-2 mb-2" data-id="element-272">
        <div className="flex-1 min-w-0" data-id="element-273">
          <p className={`text-[11px] md:text-[12px] font-bold tracking-widest uppercase ${isFeatured && selected ? 'text-cyan' : 'text-gris-claro'}`} data-id="element-274">
            {plan.name}
          </p>
        </div>

        {/* Radio indicator */}
        <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selected ? 'border-cyan bg-cyan' : 'border-gris-medio bg-transparent'}`} data-id="element-275">
          <AnimatePresence data-id="element-276">
            {selected && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} exit={{
            scale: 0
          }} data-id="element-277">
                <CheckIcon size={12} className="text-black" strokeWidth={4} data-id="element-278" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex items-baseline gap-2 mb-1" data-id="element-279">
        <p className={`text-[28px] md:text-[34px] font-extrabold leading-none ${selected ? 'text-cyan' : 'text-blanco'}`} data-id="element-280">
          {plan.price}
        </p>
        <p className="text-[12px] md:text-[13px] text-gris-claro line-through" data-id="element-281">
          {plan.priceBefore}
        </p>
      </div>

      <p className={`text-[12px] md:text-[13px] leading-snug ${isFeatured && selected ? 'text-cyan/80' : 'text-[#888]'}`} data-id="element-282">
        {plan.desc}
      </p>

      {showBenefits && <ul className="flex flex-col gap-1.5 mt-3 pt-3 border-t border-gris-medio/40" data-id="element-283">
          {plan.benefits.map((b, i) => <li key={i} className="text-[12px] md:text-[13px] text-[#bbb] flex gap-1.5 items-start" data-id="element-284">
              <CheckIcon size={13} className="text-verde shrink-0 mt-0.5 stroke-[3]" data-id="element-285" />
              <span className="leading-snug" data-id="element-286">{b}</span>
            </li>)}
        </ul>}
    </motion.button>;
};
export const PlanSelector = ({
  variant = 'full'
}: PlanSelectorProps) => {
  const {
    selectedId,
    setSelectedId,
    selectedPlan
  } = useSelectedPlan();
  const handleBuy = () => {
    window.location.href = selectedPlan.url;
  };
  return <div className="w-full max-w-[720px] mx-auto" data-id="element-287">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pt-3" data-id="element-288">
        {(['esencial', 'pro'] as PlanId[]).map(id => <PlanCard key={id} plan={PLANS[id]} selected={selectedId === id} onSelect={() => setSelectedId(id)} variant={variant} data-id="element-289" />)}
      </div>

      {/* Shared CTA */}
      <motion.div layout className="mt-5 md:mt-6" data-id="element-290">
        <motion.button type="button" onClick={handleBuy} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className="w-full bg-cyan text-black font-extrabold text-[15px] md:text-[17px] uppercase tracking-wide py-4 md:py-5 px-6 rounded-xl flex items-center justify-center gap-2.5 transition-shadow shadow-[0_0_28px_rgba(0,229,255,0.25)] hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]" data-id="element-291">
          <span data-id="element-292">💥 Quiero el {selectedPlan.name}</span>
          <ArrowRightIcon size={20} strokeWidth={3} data-id="element-293" />
        </motion.button>

        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-3 text-[11.5px] md:text-[12px] text-gris-claro" data-id="element-294">
          <span className="flex items-center gap-1" data-id="element-295">
            <CheckIcon size={12} className="text-verde stroke-[3]" data-id="element-296" />
            Pago seguro
          </span>
          <span className="flex items-center gap-1" data-id="element-297">
            <CheckIcon size={12} className="text-verde stroke-[3]" data-id="element-298" />
            Acceso en 5 min
          </span>
          <span className="flex items-center gap-1" data-id="element-299">
            <CheckIcon size={12} className="text-verde stroke-[3]" data-id="element-300" />
            Garantía 7 días
          </span>
        </div>
      </motion.div>
    </div>;
};